import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useBranchDetailsQuery } from '@/api/queries';
import { useOrganizationQuery } from '@/api/queries/organizations';
import { useCatalogImport } from '@/pages/catalogImport/store';
import { useImportWork, type ImportWork } from '@/pages/catalogImport/work';
import {
    useCreateBranch,
    type BranchCreateOutcome,
} from '@/pages/branches/list/useCreateBranch';
import { PATH } from '@/router/PATH';

/** What a branch card shows. */
export type BranchChoice = {
    id: string;
    name: string;
    address: string;
    isMain: boolean;
};

/**
 * Why the server did not create the branch, as the card words it (never the
 * server's sentence): it refused what was sent (`refused`, a 4xx), or there
 * was no answer or it failed (`failed`). bos-api's `POST /branches` refuses
 * with no code today (field validation the card already does first); a code
 * it adds later can get its own kind here.
 */
export type NewBranchFailure = 'refused' | 'failed';

/** The inline new-branch form's own error: a missing field, or the server's failure. */
export type NewBranchError =
    | { kind: 'nameRequired' }
    | { kind: 'addressRequired' }
    | { kind: 'server'; failure: NewBranchFailure };

export const newBranchFailureOf = (
    outcome: Extract<BranchCreateOutcome, { ok: false }>,
): NewBranchFailure =>
    outcome.status !== undefined &&
    outcome.status >= 400 &&
    outcome.status < 500
        ? 'refused'
        : 'failed';

/** Where Branch's Back goes: Accounting while it is still needed, else Welcome. */
export type BranchBackStep = 'accounting' | 'start';

/**
 * Changing the branch is asked: the branch it changes to, and the work on
 * the file that stays with it (`importWorkOf`).
 */
export type BranchChangeConfirm = { branch: string; work: ImportWork };

export type UseBranchStepResult = {
    branches: BranchChoice[];
    /** No list has loaded yet, and none failed. */
    isLoading: boolean;
    /** No list has loaded, and the read failed: Retry in place of the cards. */
    isLoadError: boolean;
    /**
     * A list loaded before, but the latest read failed: the cards stay as
     * they were loaded, the selection with them, and a notice offers Retry.
     */
    isRefreshError: boolean;
    onRetryLoad: () => void;
    selectedId: string | null;
    /** The selected branch, which Continue names; `null` until one is chosen. */
    selected: BranchChoice | null;
    onSelect: (id: string) => void;
    isAdding: boolean;
    onStartAdd: () => void;
    onCancelAdd: () => void;
    newName: string;
    onNewNameChange: (value: string) => void;
    newAddress: string;
    onNewAddressChange: (value: string) => void;
    newError: NewBranchError | null;
    isCreating: boolean;
    onCreate: () => void;
    /**
     * Continue with the selected branch. The branch already in the store
     * opens Upload with everything as it was, its check included; another
     * one, over a file with work, asks first (`changeConfirm`); else it is
     * written to the store and Upload opens.
     */
    onContinue: () => void;
    /**
     * Changing the branch is asked, with the work it keeps: open from
     * Continue with another branch until the owner answers. `null` when
     * nothing is asked.
     */
    changeConfirm: BranchChangeConfirm | null;
    /** Stay on this branch: closes the question and changes nothing. */
    onStayOnBranch: () => void;
    /**
     * Change branch: writes the branch, which drops only the check's
     * answer, and opens Upload, which checks the same rows again.
     */
    onConfirmBranchChange: () => void;
    /**
     * The file in the import store, by name: coming back after it was read,
     * Branch says the file and the work on it are kept. `null` without one.
     */
    keptFile: string | null;
    /** The step Back opens; its strip name is Back's label ("Back" for Welcome). */
    backStep: BranchBackStep;
    /** Opens `backStep` by path; the import store is left as it is. */
    onBack: () => void;
};

/**
 * Branch: which branch the file is for. The only branch is preselected, and
 * a branch already in the import store (coming back from Upload) stays
 * selected. A new branch is created inline through the shared create hook
 * (never the branch drawer), shown at once and selected; the list's refetch
 * then carries it. Continue writes the branch to the import store and opens
 * Upload.
 *
 * **Coming back after a check** (bos-app#209): Continue with the branch
 * already in the store only opens Upload, so the check stands and Upload
 * does not check again. Another branch keeps the file and the work on it
 * (`setBranch` drops only the check's answer, and Upload checks the same
 * rows against the new branch); when there is work (`hasWork`) it asks
 * first (`changeConfirm`), and staying changes nothing. `keptFile` names
 * the stored file for the line that says it is kept.
 *
 * Once a list has loaded it stays: a later read that fails (thrown, or
 * answered `success: false`, which would replace the query's data) keeps
 * the last list and the selection, and says so with Retry
 * (`isRefreshError`). Only a first read that fails replaces the cards.
 *
 * **Back** (bos-app#201) opens Accounting while the organisation still
 * needs an accounting start (the gate's cached read, which the start
 * re-reads on success), else Welcome: once Accounting has run it is never
 * offered again. It opens its step by path and changes nothing in the
 * store, so coming forward finds the branch still selected.
 */
export const useBranchStep = (): UseBranchStepResult => {
    const navigate = useNavigate();
    const { state, actions } = useCatalogImport();
    const branchesQuery = useBranchDetailsQuery();
    const accountingStartNeeded =
        useOrganizationQuery().data?.accountingStartNeeded === true;
    const backStep: BranchBackStep = accountingStartNeeded
        ? 'accounting'
        : 'start';
    const { create, isCreating } = useCreateBranch();

    const [pickedId, setPickedId] = React.useState<string | null>(null);
    const [created, setCreated] = React.useState<BranchChoice[]>([]);
    const [isAdding, setIsAdding] = React.useState(false);
    const [newName, setNewName] = React.useState('');
    const [newAddress, setNewAddress] = React.useState('');
    const [newError, setNewError] = React.useState<NewBranchError | null>(null);

    const answered = branchesQuery.data?.success
        ? branchesQuery.data.response.items
        : undefined;
    // The last list that loaded, adjusted during render (React's "adjust
    // state when a prop changes"): a failed read leaves it as it was.
    const [lastListed, setLastListed] = React.useState(answered);
    if (answered !== undefined && answered !== lastListed) {
        setLastListed(answered);
    }
    const listed = answered ?? lastListed;
    const isReadError =
        branchesQuery.isError || branchesQuery.data?.success === false;

    const branches = React.useMemo<BranchChoice[]>(() => {
        const fromServer = (listed ?? [])
            .filter((branch) => !branch.archived)
            .map(({ id, name, address, isMain }) => ({
                id,
                name,
                address,
                isMain,
            }));
        const known = new Set(fromServer.map((branch) => branch.id));
        return [
            ...fromServer,
            ...created.filter((branch) => !known.has(branch.id)),
        ];
    }, [created, listed]);

    const storedId = state.branch?.id ?? null;
    const selectedId =
        [pickedId, storedId].find(
            (id) => id !== null && branches.some((branch) => branch.id === id),
        ) ?? (branches.length === 1 ? (branches[0]?.id ?? null) : null);
    const selected =
        branches.find((branch) => branch.id === selectedId) ?? null;

    const onStartAdd = React.useCallback(() => setIsAdding(true), []);

    const onCancelAdd = React.useCallback(() => {
        setIsAdding(false);
        setNewName('');
        setNewAddress('');
        setNewError(null);
    }, []);

    const onNewNameChange = React.useCallback((value: string) => {
        setNewName(value);
        setNewError(null);
    }, []);

    const onNewAddressChange = React.useCallback((value: string) => {
        setNewAddress(value);
        setNewError(null);
    }, []);

    const onCreate = React.useCallback(async () => {
        const name = newName.trim();
        const address = newAddress.trim();
        if (name.length === 0) {
            setNewError({ kind: 'nameRequired' });
            return;
        }
        if (address.length === 0) {
            setNewError({ kind: 'addressRequired' });
            return;
        }

        const outcome = await create({
            name,
            address,
            // A main branch already exists; a new one never takes its place here.
            isMain: false,
            area: null,
            imageUpload: null,
            imageUrl: null,
        });
        if (!outcome.ok) {
            setNewError({
                kind: 'server',
                failure: newBranchFailureOf(outcome),
            });
            return;
        }

        setCreated((previous) => [
            ...previous,
            { id: outcome.branchId, name, address, isMain: false },
        ]);
        setPickedId(outcome.branchId);
        setIsAdding(false);
        setNewName('');
        setNewAddress('');
    }, [create, newAddress, newName]);

    // Only a stored file can carry work, and only then is a change asked.
    const {
        work,
        asksFirst: asksBeforeChanging,
        pending: pendingChange,
        setPending: setPendingChange,
    } = useImportWork<BranchChoice>();

    const changeTo = React.useCallback(
        (branch: BranchChoice) => {
            actions.setBranch({ id: branch.id, name: branch.name });
            navigate(PATH.ONBOARDING_STEP.BIND('file'));
        },
        [actions, navigate],
    );

    const onContinue = React.useCallback(() => {
        if (!selected) {
            return;
        }
        // The same branch changes nothing: the check stands.
        if (selected.id === state.branch?.id) {
            navigate(PATH.ONBOARDING_STEP.BIND('file'));
            return;
        }
        if (asksBeforeChanging) {
            setPendingChange(selected);
            return;
        }
        changeTo(selected);
    }, [
        asksBeforeChanging,
        changeTo,
        navigate,
        selected,
        setPendingChange,
        state.branch,
    ]);

    const onStayOnBranch = React.useCallback(
        () => setPendingChange(null),
        [setPendingChange],
    );

    const onConfirmBranchChange = React.useCallback(() => {
        setPendingChange(null);
        if (pendingChange) {
            changeTo(pendingChange);
        }
    }, [changeTo, pendingChange, setPendingChange]);

    const onBack = React.useCallback(
        () => navigate(PATH.ONBOARDING_STEP.BIND(backStep)),
        [backStep, navigate],
    );

    const { refetch } = branchesQuery;

    return {
        branches,
        isLoading: listed === undefined && !isReadError,
        isLoadError: listed === undefined && isReadError,
        isRefreshError: listed !== undefined && isReadError,
        onRetryLoad: () => void refetch(),
        selectedId,
        selected,
        onSelect: setPickedId,
        isAdding,
        onStartAdd,
        onCancelAdd,
        newName,
        onNewNameChange,
        newAddress,
        onNewAddressChange,
        newError,
        isCreating,
        onCreate: () => void onCreate(),
        onContinue,
        changeConfirm:
            pendingChange === null
                ? null
                : { branch: pendingChange.name, work },
        onStayOnBranch,
        onConfirmBranchChange,
        keptFile: state.file?.name ?? null,
        backStep,
        onBack,
    };
};
